import { useCallback, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Switch } from '@mui/material';
import { DocumentDescriptionField, DocumentHeader, updateDocumentField } from '../documentsApi';
import { AppId } from '../../../app/appTypes';
import { EditDescriptionField } from './EditDescriptionField';
import { EditDocumentNumberDialog } from './EditDocumentNumberDialog';
import { DocumentSettings } from './DocumentSettings';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { isDocEditable, updateDocumentHeader } from '../documentSlice';
import { DocumentKeyValueRow } from '../utils/DocumentKeyValueRow';
import { EditDocumentLanguageDialog } from './EditDocumentLanguageDialog';
import { DocumentLanguages } from '../documentTypes';
import { Localized, useLocalization } from '@fluent/react';
import { DocumentRelationsPanel } from './DocumentRelationsPanel';
import { DocumentAllProcessesPanel } from './DocumentAllProcessesPanel';
import { If } from '../../../app/If';
import { EditRichDescriptionField } from './EditRichDescriptionField';
import { EditUrsNumberingModeDialog } from '../../urs/dialogs/EditUrsNumberingModeDialog';
import { EditDocumentStructureDialog } from './EditDocumentStructureDialog';
import { StructureName } from '../../device/Structure';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { SaveButton } from '../../../app/components/SaveButton';
import { CancelButton } from '../../../app/components/CancelButton';

type DialogState
    = "none"
    | "editDocumentNumber"
    | "editDocumentLanguage"
    | "editDocumentStructure"
    | "editAdvancedWorkflow"
    | "editUrsNumberingMode"
    | { tag: 'editDescriptionField', field: DocumentDescriptionField, value: string, documentId: AppId, tiptap: boolean }

interface DocumentDetailsPanelProps {
    document: DocumentHeader
}

export const DocumentDetailsPanel = ({ document }: DocumentDetailsPanelProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const editable = useAppSelector(isDocEditable)
    const [dialog, setDialog] = useState<DialogState>("none")
    const [documentNumber, setDocumentNumber] = useState<string>(document.no);
    const [title, setTitle] = useState<string>(document.title);
    const [purpose, setPurpose] = useState<string>(document.purpose);
    const [description, setDescription] = useState<string>(document.description);
    const [language, setLanguage] = useState<DocumentLanguages>(document.language);
    const [structureId, setStructureId] = useState<AppId>(document.structureId);
    const [hasAdvancedWorkflow, setHasAdvancedWorkflow] = useState<boolean>(document.hasAdvancedWFL);
    const [hasAdvancedWorkflowDraft, setHasAdvancedWorkflowDraft] = useState<boolean>(document.hasAdvancedWFL);
    const [savingAdvancedWorkflow, setSavingAdvancedWorkflow] = useState(false);
    const [numberingMode, setNumberingMode] = useState<string | undefined>(
        document.settings.find(setting => setting.name === 'NUMBERING_MODE')?.value
    );

    const numberingModeToFluentId: Record<string, string> = {
        UNIQUE_REQ: "urs-unique",
        DECIMAL_HIERARCHICAL: "urs-hierarchical",
    };
    const numberingModeLabel = numberingMode
        ? (l10n.getString(numberingModeToFluentId[numberingMode] ?? numberingMode) || numberingMode)
        : "";

    const handleEditDescriptionFieldClick = useCallback(
        (field: 'title' | 'purpose' | 'description' | 'area' | 'language', value: string) => {
            let tiptap = false
            if (field === 'description') {
                tiptap = true
            }
            setDialog({ tag: 'editDescriptionField', field, value, documentId: document.id, tiptap })
        },
        [document.id]
    );

    const handleEditDocumentNumber = useCallback(() => {
        setDialog("editDocumentNumber")
    }, [])

    const handleEditDocumentLanguage = useCallback(() => {
        setDialog("editDocumentLanguage")
    }, [])

    const editDocumentNumberSuccess = (newValue: string) => {
        setDialog("none")
        setDocumentNumber(newValue)
        dispatch(updateDocumentHeader({ ...document, no: newValue }))
    }

    const editDocumentLanguageSuccess = (newValue: DocumentLanguages) => {
        setDialog("none")
        setLanguage(newValue)
        dispatch(updateDocumentHeader({ ...document, language: newValue }))
    }

    const editDocumentStructureSuccess = (newValue: AppId) => {
        setDialog("none")
        setStructureId(newValue)
        dispatch(updateDocumentHeader({ ...document, structureId: String(newValue) }))
    }

    const editDocumentNumberingModeSuccess = (newValue: string) => {
        setDialog("none")
        setNumberingMode(newValue)

        
        const settings = document.settings.map(setting =>
            setting.name === 'NUMBERING_MODE'
                ? { ...setting, value: newValue }
                : setting
        )

        dispatch(updateDocumentHeader({ ...document, settings }))
    }

    const editDescriptionFieldSuccess = (fieldName: DocumentDescriptionField, newValue: string) => {
        setDialog("none")
        switch (fieldName) {
            case 'title':
                setTitle(newValue)
                dispatch(updateDocumentHeader({ ...document, title: newValue }))
                break;
            case 'description':
                setDescription(newValue)
                dispatch(updateDocumentHeader({ ...document, description: newValue }))
                break;
            case 'purpose':
                setPurpose(newValue)
                dispatch(updateDocumentHeader({ ...document, purpose: newValue }))
                break;
        }
    }

    const descriptionEl = <div className='rich' dangerouslySetInnerHTML={{ __html: description }} />

    const handleAdvancedWorkflowSave = async () => {
        try {
            setSavingAdvancedWorkflow(true)
            await dispatch(updateDocumentField({
                documentId: document.id,
                fieldName: "hasAdvancedWFL",
                value: String(hasAdvancedWorkflowDraft),
            })).unwrap()

            setHasAdvancedWorkflow(hasAdvancedWorkflowDraft)
            dispatch(updateDocumentHeader({ ...document, hasAdvancedWFL: hasAdvancedWorkflowDraft }))
            dispatch(showSuccess("saved"))
            setDialog("none")
        } catch {
            dispatch(showError("error"))
        } finally {
            setSavingAdvancedWorkflow(false)
        }
    }

    const handleOpenAdvancedWorkflowDialog = () => {
        setHasAdvancedWorkflowDraft(hasAdvancedWorkflow)
        setDialog("editAdvancedWorkflow")
    }

    return (
        <>
            <Paper elevation={3} sx={{ p: 1, mb: 1 }}>
                <Box>

                    <DocumentKeyValueRow
                        label={<Localized id="document-number-long">_number</Localized>}
                        value={documentNumber}
                        editable
                        onEdit={handleEditDocumentNumber}
                    />

                    <DocumentKeyValueRow
                        label={<Localized id="document-title">_title</Localized>}
                        value={title}
                        editable
                        onEdit={() => handleEditDescriptionFieldClick("title", title)}
                    />
                    <DocumentKeyValueRow
                        label={<Localized id="description">_description</Localized>}
                        value={descriptionEl}
                        multiline
                        editable
                        onEdit={() => handleEditDescriptionFieldClick("description", description)}
                    />
                    <DocumentKeyValueRow
                        label={<Localized id="document-language">_document-language</Localized>}
                        value={language}
                        editable
                        onEdit={handleEditDocumentLanguage}
                    />
                    <DocumentKeyValueRow
                        label={<Localized id="structure">_structure</Localized>}
                        value={structureId ? <StructureName structureId={String(structureId)} /> : "-"}
                        editable
                        onEdit={() => setDialog("editDocumentStructure")}
                    />
                    <DocumentKeyValueRow
                        label="Zaawansowany workflow"
                        value={hasAdvancedWorkflow ? "Wlaczony" : "Wylaczony"}
                        editable={editable}
                        onEdit={handleOpenAdvancedWorkflowDialog}
                    />
                    {document.type === 'URS' && numberingMode !== undefined && (
                        <DocumentKeyValueRow
                            label={<Localized id="urs-numbering-method">_numberingMethod</Localized>}
                            value={numberingModeLabel}
                            editable
                            onEdit={() => setDialog("editUrsNumberingMode")}
                        />
                    )}
                    <Box mt={2}>
                        <DocumentSettings />
                    </Box>
                </Box>
                <DocumentRelationsPanel documentId={document.id} />
                <DocumentAllProcessesPanel documentId={document.id} />
            </Paper>

            {dialog === 'editDocumentNumber' && (
                <EditDocumentNumberDialog
                    documentId={document.id}
                    value={documentNumber}
                    onSuccess={editDocumentNumberSuccess}
                    onCancel={() => setDialog('none')}
                />
            )}
            {dialog === 'editDocumentLanguage' && (
                <EditDocumentLanguageDialog
                    documentId={document.id}
                    defLanguage={language}
                    onSuccess={editDocumentLanguageSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
            {dialog === 'editDocumentStructure' && (
                <EditDocumentStructureDialog
                    documentId={document.id}
                    defStructureId={structureId}
                    onSuccess={editDocumentStructureSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
            {dialog === 'editAdvancedWorkflow' && (
                <Dialog open={true} onClose={() => !savingAdvancedWorkflow && setDialog("none")} fullWidth maxWidth="xs">
                    <DialogTitle>Zaawansowany workflow</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={<Switch checked={hasAdvancedWorkflowDraft} onChange={(_, checked) => setHasAdvancedWorkflowDraft(checked)} />}
                                label={hasAdvancedWorkflowDraft ? "Wlaczony" : "Wylaczony"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <SaveButton loading={savingAdvancedWorkflow} onClick={handleAdvancedWorkflowSave} />
                        <CancelButton loading={savingAdvancedWorkflow} onClick={() => setDialog("none")} />
                    </DialogActions>
                </Dialog>
            )}
            {typeof dialog === 'object' && dialog.tag === 'editDescriptionField' && (
                <If condition={dialog.tiptap === false} otherwise={
                    <EditRichDescriptionField
                        documentId={document.id}
                        fieldName={dialog.field}
                        value={dialog.value}
                        onSuccess={editDescriptionFieldSuccess}
                        onCancel={() => setDialog("none")}
                    />
                }>
                    <EditDescriptionField
                        documentId={document.id}
                        fieldName={dialog.field}
                        value={dialog.value}
                        onSuccess={editDescriptionFieldSuccess}
                        onCancel={() => setDialog("none")}
                    />
                </If>
            )}
            {dialog === 'editUrsNumberingMode' && numberingMode !== undefined && (
                <EditUrsNumberingModeDialog
                    ursId={document.id}
                    numberingMode={numberingMode}
                    onSuccess={editDocumentNumberingModeSuccess}
                    onCancel={() => setDialog("none")}
                />
            )}
        </>
    );
};
